import React from 'react';
import { Truck, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import { formatINR } from '@/shared/utils/currency';
import type { DispatchItem } from './types';

interface PendingDispatchWidgetProps {
  dispatches: DispatchItem[];
}

export const PendingDispatchWidget: React.FC<PendingDispatchWidgetProps> = ({ dispatches }) => {
  const navigate = useNavigate();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ready':
        return 'bg-success/10 text-success-text border-success/30';
      case 'packed':
        return 'bg-info/10 text-info-text border-info/30';
      case 'in_process':
        return 'bg-warning/10 text-warning-text border-warning/30';
      default:
        return 'bg-surface-variant text-on-surface-variant border-border';
    }
  };

  return (
    <div className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between border-b border-outline/80 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-info/10 text-info-text">
              <Truck className="h-4 w-4" />
            </div>
            <div>
              <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
                Pending Dispatch
              </span>
              <div className="text-data-xs text-on-surface-subtle">My confirmed orders awaiting delivery</div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate(ROUTES.salesDispatchTracking)}
            className="inline-flex items-center gap-1 text-data-xs font-semibold text-primary hover:underline cursor-pointer"
          >
            <span>View All</span>
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>

        {/* Table Rows */}
        {dispatches.length === 0 ? (
          <div className="my-6 text-center text-body-sm text-on-surface-subtle">No confirmed orders awaiting dispatch.</div>
        ) : (
          <div className="my-2 overflow-x-auto">
            <table className="w-full text-left text-body-sm">
              <thead>
                <tr className="border-b border-border text-data-xs font-semibold text-on-surface-subtle">
                  <th className="pb-2 font-medium">ORDER #</th>
                  <th className="pb-2 font-medium">CLIENT</th>
                  <th className="pb-2 font-medium">DESTINATION</th>
                  <th className="pb-2 font-medium">AMOUNT</th>
                  <th className="pb-2 text-right font-medium">STATUS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {dispatches.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => navigate(ROUTES.salesOrderDetail.replace(':id', item.id))}
                    className="group cursor-pointer transition hover:bg-surface-variant"
                  >
                    <td className="py-2.5 pr-2 font-semibold text-primary group-hover:underline">
                      {item.orderNumber}
                    </td>
                    <td className="py-2.5 pr-2">
                      <div className="font-semibold text-on-surface">{item.client}</div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-data-xs text-on-surface-subtle">{item.itemsCount} line{item.itemsCount === 1 ? '' : 's'}</span>
                        {item.isOverdue ? (
                          <span className="text-data-xs font-semibold text-danger-text">
                            · 🚨 Overdue by {item.overdueDays}d (Due: {item.dueFormatted})
                          </span>
                        ) : item.dueFormatted ? (
                          <span className="text-data-xs text-on-surface-subtle">
                            · Due: {item.dueFormatted}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-2.5 pr-2 text-data-xs text-on-surface-variant">
                      {item.shippingCity || '—'}
                    </td>
                    <td className="py-2.5 pr-2 font-semibold text-on-surface">{formatINR(item.amount)}</td>
                    <td className="font-mono py-2.5 text-right tabular-nums text-data-sm">
                      <span
                        className={`inline-block rounded-md border px-2 py-0.5 text-data-xs font-semibold capitalize ${getStatusBadge(
                          item.status
                        )}`}
                      >
                        {item.statusLabel}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Awaiting dispatch:</span>
        <span className="font-semibold text-success-text">
          {dispatches.length} order{dispatches.length === 1 ? '' : 's'} · {formatINR(dispatches.reduce((s, d) => s + d.amount, 0))}
        </span>
      </div>
    </div>
  );
};
